import React, { FC } from 'react'
import cn from 'classnames'

import { BlockTypes, ContentfulBlock } from '../../components/ContentfulBlock/types'
import { media } from '../../style/media'
import { Block, Page } from '../../components'
import { DynamicContentFactory } from '../../components/DynamicContentFactory'
import { ErrorPage404 } from '..'
import { ComposedPageFields } from './types'
import { ContentfulEntry, convertLocaleToContentfulLocale, validatePageAccess } from '@vrw/data/src/utils/contentfulPages'
import { getLocale } from '../../dataImplementation/viewingRegionWeb'
import { useParams } from 'react-router-dom'
import { useAppSelector } from '../../redux/hooks/hooks'
import { getIsAuthenticated } from '@vrw/data/src/redux/auth/selectors'
import { useContentBySlug } from '../../query/contentful/useContentPageBySlug'
import { YStack, Spinner, styled } from '@red-ui/components'

const SpinnerContainer = styled(YStack, {
  minHeight: '80vh',
  justifyContent: 'center',
})

// These slugs should not be shown to users directly
export const restrictedSlugs = [
  'home-page-logged-out',
  'home-page-logged-in',
  'home-page-logged-out-us',
  'home-page-logged-in-us',
  'footer',
]

export const ComposedPage: FC = () => {
  const locale = getLocale()
  const { pagePath = '' } = useParams()
  const isAuthenticated = useAppSelector(getIsAuthenticated)
  const isRestrictedSlug = restrictedSlugs.includes(pagePath)

  const {
    data: content,
    seo,
    isLoading,
    isError,
    metadata,
  } = useContentBySlug<ComposedPageFields>({
    slug: pagePath,
    viewingRegion: convertLocaleToContentfulLocale(locale),
  })

  // Check if page access is allowed based on tags
  const accessAllowed = content?.sys
    ? validatePageAccess({
        tags: metadata?.tags,
        locale,
        isAuthenticated,
      })
    : true

  if (isLoading) {
    return (
      <SpinnerContainer>
        <Spinner size="large" color="$red6" />
      </SpinnerContainer>
    )
  }

  if (isError || isRestrictedSlug || !accessAllowed || !content) {
    return <ErrorPage404 />
  }

  // Create a compatible object for DynamicContentFactory
  const makeContentTypeCompatible = (block: ContentfulBlock<any>): ContentfulEntry => {
    return {
      ...block,
      contentTypeId: block.sys?.contentType?.sys.id ?? '',
      fields: block.fields ?? {},
    } as ContentfulEntry
  }

  return (
    <>
      <style jsx>{`
        .block-container {
          margin-bottom: 24px;
        }
        .richtext-wrapper {
          padding: 0 15px;
        }
        .redhotbanner-wrapper,
        .image-embed-wrapper,
        .how-it-works-wrapper,
        .faq-wrapper {
          margin-bottom: 0;
        }
        @media ${media.tabletAndHigher} {
          .block-container {
            margin-bottom: 36px;
          }
          .richtext-wrapper {
            padding: 0;
          }
          .redhotbanner-wrapper,
          .image-embed-wrapper,
          .how-it-works-wrapper,
          .faq-wrapper {
            margin-bottom: 0;
          }
        }
        @media ${media.desktopAndHigher} {
          .block-container {
            margin-bottom: 48px;
          }
          .redhotbanner-wrapper,
          .image-embed-wrapper,
          .how-it-works-wrapper,
          .faq-wrapper {
            margin-bottom: 0;
          }
        }
      `}</style>
      <Page
        testId={`composed-page-${pagePath}`}
        title={`${seo?.fields?.title ?? ''} | Virgin Red `}
        metaDescription={`${seo?.fields?.description ?? ''}`}>
        {content.sys.contentType?.sys.id === 'dynamicPageContent' ? (
          <DynamicContentFactory content={makeContentTypeCompatible(content)} />
        ) : (
          <div className="page-container">
            {content.fields?.body?.map((x: ContentfulBlock<any>) => (
              <div
                key={x.sys.id}
                className={cn(
                  'block-container',
                  { 'richtext-wrapper': x.sys.contentType?.sys.id === BlockTypes.RICH_TEXT_BLOCK },
                  { 'redhotbanner-wrapper': x.sys.contentType?.sys.id === BlockTypes.RED_HOT_BANNER },
                  { 'image-embed-wrapper': x.sys.contentType?.sys.id === BlockTypes.IMAGE_EMBEDDED_CONTENT },
                  { 'how-it-works-wrapper': x.sys.contentType?.sys.id === BlockTypes.HOW_IT_WORKS },
                  { 'faq-wrapper': x.sys.contentType?.sys.id === BlockTypes.FAQ_BLOCK }
                )}>
                <Block block={makeContentTypeCompatible(x)}></Block>
              </div>
            ))}
          </div>
        )}
      </Page>
    </>
  )
}
